/**
 * Response shapes from api/routes/leaderboard.py.
 *
 * `btl_score` is `number | string` because it is a Postgres `numeric`: psycopg
 * hands it over as a Decimal and _serialisable() stringifies it rather than lose
 * precision. `elo` is `double precision` and stays a number. Do not "fix" this
 * by declaring both as number — the string arrives at runtime regardless of what
 * the type says.
 */

export interface RunSummary {
  run_id: string
  thread_id: string
  status:
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'empty'
    | 'blocked'
    | 'failed'
    | 'cancelled'
    | 'salvaged'
    | string
  stage: string | null
  prompt: string | null
  started_at: string | null
  updated_at: string | null
  finished_at: string | null
  last_error: string | null
  /** Set once a stop has been requested; the sweep aborts within ~10s. */
  cancel_requested_at: string | null
  /**
   * Token counters, and the cost derived from them by the API. `cost_usd` is
   * NOT a stored column — leaderboard/cost.py prices the counters per request,
   * so a price correction re-prices every historical run on the next reload.
   *
   * All of these are 0/null for runs recorded before infra/sql/002_run_usage.sql,
   * which genuinely have no measurement rather than a measurement of zero.
   */
  llm_calls: number
  input_tokens: number
  output_tokens: number
  cached_input_tokens: number
  cache_write_tokens: number
  cost_usd: number
  /** Share of input tokens served from cache, 0–1, or null when nothing ran. */
  cache_hit_rate: number | null
  /**
   * Which instruction files the sweep read: a content hash of the `context/`
   * tree (rubric, program scope, red flags, glossary) and whether it came from
   * the image or from the mutable S3 mirror a scientist can publish to without a
   * redeploy.
   *
   * Optional, not `| null` alone: the columns arrive with
   * infra/sql/007_context_version.sql, so a deployment whose migration has not
   * run yet omits the keys entirely rather than sending null. `'unknown'` is a
   * real value and means the tree could not be read — distinct from a run that
   * never recorded it.
   */
  context_version?: string | null
  context_source?: 'baked' | 's3' | string | null
  /**
   * When the knowledge graph this sweep read was built: `YYYY-MM-DD`, or the
   * literal `'unknown'`. The companion to `context_version` — that one names the
   * rubric, this one names the data, and two runs a month apart can disagree
   * because the graph was rebuilt between them.
   *
   * `'unknown'` is a finding, not a missing value: neither the graph nor
   * `KG_BUILD_DATE` supplied a date, so the run may not draw any post-build
   * literature boundary. Absent/null instead means the row predates
   * infra/sql/011_kg_build_date.sql and never recorded it — deliberately not
   * backfilled.
   */
  kg_build_date?: string | null
  /**
   * Where `kg_build_date` came from: `'graph'` (the graph's own build manifest,
   * measured at sweep start), `'config'` (this deployment's `KG_BUILD_DATE`, told
   * to us and not verifiable from the run) or `'unknown'` (neither).
   *
   * The pair never travels apart, because "built X per the graph" and "built X per
   * a Terraform variable" license different confidence and a date whose source went
   * unrecorded is one nobody can audit. Absent/null means the row predates
   * infra/sql/025_run_vintage_source.sql — again not backfilled, since a run that
   * recorded `config` and one that was never asked are different facts.
   */
  vintage_source?: 'graph' | 'config' | 'unknown' | string | null
}

export interface RunDetail extends RunSummary {
  target_count: number
}

/** Response from POST /leaderboard/runs/{id}/cancel. */
export interface CancelResult {
  run_id: string
  /** `requested` — the live sweep will notice; `reaped` — its owner was gone. */
  outcome: 'requested' | 'reaped' | string
  status: string | null
  message: string
}

export interface DeleteResult {
  run_id: string
  outcome: 'deleted' | string
  message: string
}

/** Response from POST /leaderboard/board/{target_key}/retire. */
export interface RetireResult {
  retired: boolean
  target_key: string
  protein?: string | null
  /**
   * `false` means nothing was written — no pool. It is NOT the same as
   * `retired: false`, which means the store was reachable and declined, so a caller
   * reporting "not retired" without this field cannot say whether to retry.
   */
  persisted?: boolean
  error?: string
}

/**
 * Response from POST /leaderboard/runs/{run_id}/retire.
 *
 * `retired` is a count and `0` is a success — a run that never reached a writer, or
 * whose rows were already retired. The API deliberately does not 404 on it, because
 * that would claim the run does not exist.
 */
export interface RetireRunResult {
  retired: number
  run_id: string
  target_keys?: string[]
  persisted?: boolean
  error?: string
}

export interface Evidence {
  type: string
  id?: string
  url?: string
  note?: string
}

export interface Target {
  id: number
  run_id: string
  hypothesis_id: string
  protein: string
  accession: string | null
  pathway: string | null
  moa: string | null
  elo: number | null
  btl_score: number | string | null
  tier: string | null
  rank: number | null
  rationale: string | null
  evidence: Evidence[] | null
  created_at: string | null
  /**
   * `null` or `'incomplete'`, written by the harness from the same near-miss
   * queue the review gate reads — never from the model's ranking payload. There
   * is no `'passed'`: absence of a flag IS the pass, so render `null` as nothing
   * at all rather than as "unknown". `'failed'` cannot appear here — it is
   * excluded from the tournament, so it has no ranked row; see `DeadEnd`.
   *
   * Null on every row written before infra/sql/006_target_flags.sql, which did
   * not record the distinction rather than recording its absence.
   */
  review_verdict: string | null
  /** What went unmeasured on an `incomplete` row: fields to go and fill. */
  review_gaps: string[] | null
  /**
   * `null`, `'regulatory'` or `'complex'` — the model's own scoping judgement
   * per context/target-criteria §6. Out-of-scope target types are *penalised,
   * not filtered*, so a flagged target sits low on the board rather than missing
   * from it. Showing the badge is what tells a reader why a good target ranks low.
   */
  scope_flag: string | null
  /**
   * The rubric's own two numbers, from infra/sql/012_score_and_coverage.sql.
   *
   * `score` is the weighted MEAN over the axes that were *measured* (0–100, per
   * context/target-criteria §4) and `coverage` is the share of the rubric's weight
   * those axes carried (0.0–1.0). **They travel together or not at all**: a score
   * without its coverage reads as a stronger measurement than it is, which is the
   * ambiguity that migration exists to remove, so nothing here may render or export
   * one without the other.
   *
   * Null on every row written before the migration — genuinely no score, as opposed
   * to a score of zero.
   *
   * Optional rather than `| null`: the API projects `SELECT *`, so a deployment
   * whose migration has not run omits the keys entirely instead of sending null.
   */
  score?: number | null
  coverage?: number | null
  /**
   * One key per rubric axis, 0–100 after adjustments, `null` where the axis was not
   * measured. The nulls are load-bearing and must not be coerced to 0: an absent
   * axis is *why* coverage is below 100%.
   *
   * Carried by the response and deliberately neither rendered nor exported: the
   * axis set is a property of the rubric rather than of this schema, so a fixed
   * column list here would drift the first time the rubric is re-weighted. Declared
   * because a reader of this type would otherwise conclude the API cannot supply the
   * components behind `score`, and re-scoring a stored ranking is the whole reason
   * they are stored.
   */
  axis_scores?: Record<string, number | null> | null
  /**
   * Ligand starting points — from infra/sql/020_ligand_starts.sql. Stamped by the
   * harness at the leaderboard write, never sent by the model.
   *
   * **Render the three together or none of them.** `ligand_starts: []` with
   * `ligand_start_source: 'no_ligand'` is a measurement — structures were searched
   * and carried nothing worth ordering an analog of. `ligand_starts: null` with
   * `'catalog_unreachable'` is a service that did not answer. A cell that shows
   * both as a dash has collapsed a finding into a gap, and this column is one a
   * chemist acts on.
   *
   * `ligand_start_count` is `0` only when the search ran, and `null` whenever it
   * did not — never `0` for an unrun search. All three keys are absent on a
   * deployment whose migration has not run (the API projects `SELECT *`), which is
   * a third state again and also not "none found".
   */
  ligand_starts?: LigandStart[] | null
  ligand_start_count?: number | null
  ligand_start_source?: string | null
}

/**
 * One co-crystallised ligand and the catalog compounds similar to it.
 *
 * `structure_provenance` is the field that decides what may be claimed:
 * `'anchor'` is a structure of this protein, `'homolog'` is a structure of a
 * different one that cleared both the identity and the coverage floor. A homolog
 * record names its organism, and presenting it as the target's own structure is a
 * claim nobody measured — so the organism and the two numbers are rendered with
 * the PDB id, not behind it.
 */
export interface LigandStart {
  pdb_id: string
  structure_provenance: 'anchor' | 'homolog'
  structure_organism: string | null
  structure_identity: number
  structure_coverage: number
  comp_id: string
  ligand_name: string | null
  smiles: string
  /**
   * Why the molecule was kept. `'subject_of_investigation'` is RCSB's own curated
   * annotation; `'drug_like'` is this repo's heavy-atom heuristic and says so, so a
   * reader can tell a real ligand from a large buffer component that got through.
   */
  kept_because: 'subject_of_investigation' | 'drug_like'
  heavy_atoms: number | null
  formula_weight: number | null
  /** RCSB's annotation as returned, `'Y'` or `'N'` — what `kept_because` was read from. */
  is_subject_of_investigation: string | null
  /**
   * Absent when the catalog was not searched (`ligand_start_source` is
   * `'catalog_unreachable'`): the ligand stands, the orderable answer does not.
   * `[]` means it was searched and returned nothing.
   */
  catalog_hits?: CatalogHit[] | null
  /** The request behind `catalog_hits` — datasets, `top_k`, url, `measured_at`. */
  catalog_query?: Record<string, unknown> | null
}

/**
 * One orderable compound. `similarity` is the search's own number and `tier` is a
 * band over it (`exact` ≥0.99, `close` ≥0.70, `analog` ≥0.50, `weak` below) — not a
 * confidence and not potency. `catalog_id` is what a chemist quotes to order; it is
 * the id the catalog returned and is **not** a CPD number or a Lot.
 */
export interface CatalogHit {
  source: string | null
  catalog_id: string | null
  compound_id: string
  /** The hit's own structure. `null` on hits measured before it was stored. */
  smiles?: string | null
  similarity: number | null
  tier: string | null
}

/**
 * One order posted to Simmate from a board row (`leaderboard.orders`), as the list
 * route returns it.
 */
export interface CompoundOrder {
  id: number
  target_key: string
  recipient: string
  status: 'queued' | 'submitting' | 'submitted' | 'failed'
  /** The list route sends the count; the form itself stays server-side. */
  compound_count: number
  simmate_order_title: string | null
  simmate_ids: number[] | null
  error: string | null
  requested_at: string
  emailed_at: string | null
}

export interface TargetPage {
  run_id: string
  total: number
  limit: number
  offset: number
  sort: SortColumn
  direction: SortDirection
  targets: Target[]
}

/**
 * One review outcome that did not become a ranked target — or that became one
 * while still carrying gaps. From GET /runs/{run_id}/dead-ends.
 *
 * `verdict` is the whole point of the row, and the three values are three
 * different results rather than three shades of one. Rendering them under a
 * single "disqualified" heading is wrong about all three:
 *
 * - `'failed'` — a supported disqualifier fired. Excluded from the tournament
 *   hard. This is a *result*, not a gap.
 * - `'incomplete'` — too few decidable axes reached a verdict. Admitted to the
 *   tournament flagged, so this hypothesis may ALSO appear in the ranked table.
 *   That is not a contradiction and must not be rendered as one: it competed
 *   carrying its gaps.
 * - `'unreviewed'` — the harness tried twice and could not measure. An
 *   infrastructure failure, deliberately NOT `'incomplete'`: reading a transient
 *   401 as an evidence gap dresses an outage as science, in the direction that
 *   looks like a finding. It was admitted flagged, because failing open loudly
 *   beats deleting real work on a blip.
 */
export interface DeadEnd {
  run_id: string
  hypothesis_id: string
  verdict: 'failed' | 'incomplete' | 'unreviewed' | string
  protein: string | null
  mechanism: string | null
  /** The axis that fired, for `failed`. Null for the other two. */
  axis: string | null
  iteration: number | null
  /** Why it was ruled on, ordered as review produced them. */
  reasons: string[] | null
  /** What was never measured. Populated for `incomplete`. */
  gaps: string[] | null
  /**
   * Which writer the row came from: `'harness'` — a verdict from the review
   * ledger; `'narrated'` — one reconstructed from the dead-end registry alone,
   * which the reviewer subagent used to be able to append itself. Null on rows
   * written before infra/sql/009_review_coverage.sql.
   *
   * Carried by the response and not yet rendered. Declared because a reader of
   * this type would otherwise conclude the API cannot tell the two apart, and
   * that distinction is the whole reason the column exists.
   */
  source?: 'harness' | 'narrated' | string | null
  /**
   * Per failing axis, the field it could not support, as
   * `axis(D6): the field | settle: what would settle it`, joined by `"; "`.
   *
   * `unstated` means the reviewer's FAIL named no field — which was **100% of
   * drops by construction** until migration 016, because the parser never read
   * the field. Null is pre-016 and is not a pass: nothing was backfilled.
   */
  failed_check?: string | null
  /** The reviewer's own work list: what measurement would change the verdict. */
  would_settle_it?: string | null
  /**
   * `json` | `partial` | `unparseable` | `absent`, summarised
   * least-trustworthy-wins across the failing axes. Anything but `json` means the
   * drop may be the harness's own failure rather than a finding — the recap mode
   * selects on it.
   */
  verdict_source?: string | null
  /**
   * One object per failing axis. Structured rather than joined because a drop can
   * fail two axes and an export needs each value in its own column, and because
   * `reasons` beside it is a list of *rendered bullets*, which is a different
   * thing from the values behind them. `[]` means not recorded — a narrated
   * `append_dead_end` record, or a pre-016 row.
   */
  findings?: DeadEndFinding[] | null
  /**
   * Why this recorded drop is no longer the run's answer, or null when it still is.
   * Derived by the API from the run's own ranking and stored nowhere — `dead_ends`
   * is append-only, and a row a later iteration reversed is still a true record of
   * what review concluded at the time.
   *
   * - `ranked`   — the hypothesis has a ranked row: a later review reversed the drop,
   *   or an `incomplete` one competed carrying its gaps.
   * - `refined`  — a ranked target names it in `parent_ids`; evolution is
   *   non-destructive, so the parent stays dropped while the child competes.
   * - `absorbed` — a ranked target folded it away as a duplicate.
   *
   * **Read with `supersession_source`, never alone.** `run-7518d142` held 51 rows
   * for 17 hypotheses that finally failed to rank, and rendering all 51 as drops is
   * what this field exists to stop.
   */
  superseded_by?: 'ranked' | 'refined' | 'absorbed' | string | null
  /**
   * How `superseded_by` was arrived at: `ranking` — compared against a real ranking;
   * `no_ranking` — the run ranked nothing, so nothing superseded anything and every
   * row is final (a finding, not a gap); `store_unreachable` — the ranking could not
   * be read, so a null `superseded_by` claims NOTHING and must not render as final.
   */
  supersession_source?: 'ranking' | 'no_ranking' | 'store_unreachable' | string | null
  created_at: string | null
}

/** One failing axis inside `DeadEnd.findings`. Every field may be absent. */
export interface DeadEndFinding {
  axis?: string | null
  disqualifier?: string | null
  failed_check?: string | null
  evidence?: string | null
  would_settle_it?: string | null
  verdict_source?: string | null
}

export interface DeadEndPage {
  run_id: string
  total: number
  /**
   * Per-verdict totals for the WHOLE run, keyed by verdict, every known verdict
   * present at 0. Server-side from store.DEAD_END_VERDICTS.
   *
   * Do not compute these from `dead_ends` instead: that array is one capped page,
   * so a client-side count silently under-reports every run bigger than the page.
   * `dead_ends` is ordered so each verdict's rows are contiguous, which is what
   * lets a grouped rendering page without a group appearing twice.
   */
  counts: Record<string, number>
  /**
   * The same totals with superseded rows removed — what the run *concluded*, where
   * `counts` is what it *recorded*. Both are shipped because they answer different
   * questions and neither derives the other.
   *
   * `counts: null` means the ranking could not be read: show the recorded totals and
   * say the final ones are unavailable. Zeros there would report an outage as a run
   * that ruled nothing out.
   */
  final: {
    source: 'ranking' | 'no_ranking' | 'store_unreachable' | string
    counts: Record<string, number> | null
    recorded: number | null
    superseded: number | null
  }
  limit: number
  offset: number
  dead_ends: DeadEnd[]
}

/**
 * One verdict the harness recorded, **pass included**. From
 * GET /runs/{run_id}/review-log and GET /review-log.
 *
 * This is the ledger, not the archive: `/dead-ends` holds only what was ruled out,
 * so before this table existed "reviewed and cleared" and "never reviewed" were one
 * observation. A hypothesis absent from here was never reviewed — which is a
 * finding, not a gap in the data.
 */
export interface ReviewLedgerEntry {
  run_id: string
  hypothesis_id: string
  /** `passed` | `failed` | `incomplete` | `unreviewed`. */
  verdict: string
  /** Axes that returned a judgement, out of `decidable` — never out of all nine. */
  decided: number | null
  /**
   * The axes whose evidence was present, computed BEFORE dispatch. The
   * denominator of the consensus floor, which is why it is a property of the
   * record and not of the model's mood.
   */
  decidable: number | null
  review_coverage: number | null
  /** Per-axis verdicts, keyed by axis name. */
  axes: Record<string, unknown> | null
  /** Axis names that returned no judgement. */
  unchecked: string[] | null
  iteration: number | null
  /**
   * `harness` for an entry the review runner wrote, `narrated` for one recovered
   * from a model-written registry — a weaker statement, and the reason the two are
   * distinguished at all.
   */
  source: string | null
  /**
   * Why the harness could not measure, on an `unreviewed` entry. Present here so a
   * transient infrastructure failure is not read as an evidence gap.
   */
  error: string | null
  /**
   * Per failing axis, the field it judged, as `axis(D6): the field`. `unstated`
   * means the FAIL named none. Null is pre-016 and is not a pass — nothing was
   * backfilled.
   */
  failed_check?: string | null
  /** The reviewer's work list: the measurement that would change the verdict. */
  would_settle_it?: string | null
  /**
   * `json` | `partial` | `unparseable` | `absent`, least-trustworthy-wins. Anything
   * but `json` means the verdict is not citable as written.
   */
  verdict_source?: string | null
  recorded_at: string | null
  created_at: string | null
}

export interface ReviewLogPage {
  run_id: string
  total: number
  /** Whole-run per-verdict totals, every verdict present at 0 — the `/dead-ends` rule. */
  counts: Record<string, number>
  /**
   * Off the run row, measured **before** the gate reviewed the remainder, so
   * legitimately smaller than `total`. `null` means the run never reached a gate,
   * which is distinct from `0`.
   */
  reviewed_count: number | null
  pool_count: number | null
  limit: number
  offset: number
  entries: ReviewLedgerEntry[]
}

/**
 * The same ledger across every run, newest first — the manual recheck.
 *
 * No `counts` block, deliberately: a filtered cross-run tally by verdict would read
 * as an estate-wide review summary, and runs enter the table on wildly different
 * pool sizes. `total` is the count under this filter and is the only thing that
 * separates an exhausted list from a capped page.
 */
export interface CrossRunReviewLogPage {
  verdict: string | null
  since: string | null
  total: number
  limit: number
  offset: number
  entries: ReviewLedgerEntry[]
}

/**
 * One KG field this run found wrong, with the external source it cited. From
 * GET /runs/{run_id}/kg-contradictions.
 *
 * A **candidate correction, not an applied one**: nothing in this system writes
 * to Neptune, so the graph still asserts `kg_value` and this run's scores were
 * computed from it. Filing changed no rank, which is why there is no link from
 * here to a target's position.
 *
 * Keyed on `(accession, kg_field)` server-side, not on a hypothesis — the same
 * wrong triple noticed from two hypotheses is one fact about the graph.
 * `hypothesis_id` is context and may be null.
 */
export interface KgContradiction {
  run_id: string
  accession: string
  /** The predicate as the graph names it, e.g. `hasKnownLigand`. */
  kg_field: string
  /** What the graph asserts. Text, because a predicate's range varies. */
  kg_value: string | null
  contradicting_value: string | null
  protein: string | null
  hypothesis_id: string | null
  /**
   * At least one entry, always: the tool refuses a filing with no named source.
   * `type` is one of literature|structure|database|sequence|computed.
   */
  evidence: { type: string; id: string; url?: string | null; note?: string | null }[]
  /** The filer's confidence that THE GRAPH IS WRONG — not that the target is good. */
  confidence: string | null
  note: string | null
  created_at: string | null
}

export interface KgContradictionPage {
  run_id: string
  total: number
  limit: number
  offset: number
  kg_contradictions: KgContradiction[]
}

export interface LatestBoard {
  run: RunSummary | null
  total: number
  targets: Target[]
}

/**
 * One requested per-target report. From GET /runs/{run_id}/reports and from the
 * POST that queues one.
 *
 * The row IS the job: the API inserts it `queued`, the AgentCore runtime moves it
 * `running` → `sent` | `failed`, and this UI polls it. Nothing else connects the
 * two containers.
 *
 * `recipient` comes from the requester's verified Entra token, never from the
 * request body — the UI cannot choose where a report goes, it can only show where
 * this one went. Render it: a user who signed in as someone else needs to see
 * that before waiting ten minutes for mail that lands elsewhere.
 *
 * `s3_key` is deliberately absent — the client never addresses the bucket. Use
 * `downloadable` to decide whether to offer the download, not `status === 'sent'`
 * alone: a report can be `sent` with the document expired out of the bucket by
 * the 90-day lifecycle rule, and the API computes the conjunction.
 */
export interface ReportRow {
  id: number
  run_id: string
  target_id: number
  protein: string | null
  requested_by: string
  recipient: string
  status: 'queued' | 'running' | 'sent' | 'failed' | string
  error: string | null
  llm_calls: number
  input_tokens: number
  output_tokens: number
  cached_input_tokens: number
  cache_write_tokens: number
  /** Derived per request by leaderboard/cost.py, not a stored column. */
  cost_usd: number
  downloadable: boolean
  requested_at: string | null
  updated_at: string | null
  sent_at: string | null
}

export interface ReportPage {
  run_id: string
  limit: number
  offset: number
  reports: ReportRow[]
}

/**
 * POST /runs/{run_id}/targets/{target_id}/report.
 *
 * `created: false` is a success, not an error: a report for this target was
 * already queued or running, and the second click gets the existing row rather
 * than starting a second multi-dollar job. `message` is written for the user and
 * says which case it was — show it.
 */
export interface ReportRequestResult {
  created: boolean
  report: ReportRow
  message: string
}

/** Statuses for which the UI must keep polling. */
export const REPORT_IN_FLIGHT: readonly string[] = ['queued', 'running']

/**
 * Mirrors store.MAX_LIMIT — the API 422s above it. A single request can no
 * longer be assumed to hold a whole run: removing the ten-target convention
 * means a sweep ranks every review survivor, so the tables page.
 */
export const MAX_LIMIT = 500
export const PAGE_SIZES = [50, 100, 250] as const
export const DEFAULT_PAGE_SIZE = 100

/**
 * Must stay in sync with store.SORTABLE_COLUMNS — the API rejects anything else
 * with a 400 rather than silently sorting by rank, so a drift here is a visible
 * error rather than wrong-looking data.
 */
export const SORT_COLUMNS = [
  'rank',
  'elo',
  'btl_score',
  'protein',
  'tier',
  'pathway',
  'created_at',
] as const

export type SortColumn = (typeof SORT_COLUMNS)[number]
export type SortDirection = 'asc' | 'desc'

/**
 * One row of the cumulative board: what every run so far says about a target
 * identity, as opposed to `Target`, which is what ONE run said about it.
 *
 * `latest_*` and `best_*` both exist on purpose. Latest alone lets a shallow
 * sweep bury a thorough one's result; best alone ratchets upward forever, so a
 * target later shown to be weaker keeps its old high score. Showing both is the
 * only honest summary of a disagreement between runs.
 */
export interface BoardEntry {
  /** `protein|mechanism`, the primary key. Contains a pipe, and may contain
   *  slashes — encode it before putting it in a path. */
  target_key: string
  protein_key: string
  mechanism_key: string
  protein: string
  accession: string | null
  pathway: string | null
  moa: string | null
  rationale: string | null
  /** Every spelling any run used for this protein; the key is what they agreed
   *  on, not what any of them wrote. */
  aliases: string[] | null
  /**
   * Runs of THIS harness that ranked the target. Label it "runs" in the UI and
   * never "independent confirmations": these runs share a knowledge graph, a
   * rubric and a model, so their agreement is not replication. `distinct_prompts`
   * is the qualifier that makes the number readable — five runs from one prompt
   * is weaker evidence of anything than two runs from two.
   */
  runs_identified: number
  distinct_threads: number
  distinct_prompts: number
  first_seen_run: string | null
  first_seen_at: string | null
  last_seen_run: string | null
  last_seen_at: string | null
  latest_elo: number | null
  best_elo: number | null
  worst_elo: number | null
  latest_btl: number | string | null
  best_btl: number | string | null
  /**
   * The rubric score and its coverage, latest and best, from
   * infra/sql/012_score_and_coverage.sql. Same latest/best reasoning as the Elo
   * pair above, and the same pairing rule as on `Target`: a score is exported and
   * read beside its coverage or not at all.
   *
   * `best_coverage` is an independent maximum and is deliberately NOT the coverage
   * of the run that produced `best_score` — as with `best_elo` and `best_tier`. A
   * reader wanting one run's pair reads that run's row.
   */
  latest_score?: number | null
  best_score?: number | null
  latest_coverage?: number | null
  best_coverage?: number | null
  latest_tier: string | null
  best_tier: string | null
  latest_rank: number | null
  best_rank: number | null
  evidence: Evidence[] | null
  /** Newest first. The audit trail behind `runs_identified`. */
  run_ids: string[] | null
  /**
   * Champions tournaments this target competed in — a projection of
   * `leaderboard.champions`, from infra/sql/018_champions.sql.
   *
   * Read it with `champions_corroboration_note`, which the API ships with every
   * board response. It is a **weaker** statement than `runs_identified`, not a
   * stronger one: the pool was selected on those same runs' verdicts, so the
   * agreement is more correlated. Never label it a confirmation count.
   */
  champions_rounds?: number
  /**
   * Elo and rank from the LATEST champions session this target entered. Shown
   * BESIDE `best_elo`, never in place of it: a per-run Elo is a property of the
   * tournament that produced it, while a champions Elo is a different quantity —
   * cross-run, one pool, one rubric digest — and the two are not comparable.
   */
  champion_elo?: number | null
  champion_rank?: number | null
  champion_session?: string | null
  /**
   * The latest rationale refresh, from the champions log. Shown beside
   * `rationale`, never substituted for it — a re-wording of stored evidence is
   * not what the run itself recorded.
   */
  champion_rationale?: string | null
  /**
   * The champions recheck's score, from infra/sql/022_champion_score.sql. A THIRD
   * figure beside `latest_score` and `best_score`, and never a substitute for
   * either: those two are what a run's own review measured, and this one is what a
   * champions session arrived at from the stored record.
   *
   * `champion_score_source` is what makes it readable and must be rendered with it:
   * `run` means the recheck re-weighted axis scores a review actually measured,
   * `rederived` means a model was handed the stored rationale and asked to
   * reconstruct them. Both are legitimate; they are not the same claim, and a
   * `rederived` number shown as a score is a fabricated measurement.
   *
   * Unlike `best_score`/`best_coverage`, the score and coverage here are ONE
   * observation's pair and may be printed together.
   *
   * `champion_score_session` cites the session that produced it, which is not
   * necessarily `champion_session` — that is the latest session the target entered
   * the *pool* in, and the recheck runs over the whole scan.
   */
  champion_score?: number | null
  champion_coverage?: number | null
  champion_score_source?: string | null
  champion_score_session?: string | null
  /**
   * THREE-STATE, and the reason this is `boolean | null` rather than `boolean`:
   * `null` means no recap has examined this target, `false` means one has and
   * found no disqualification, `true` means at least one run ruled it out.
   * Rendering null and false the same way would make "never examined" and
   * "examined and clean" one value. Populated by the recap mode.
   */
  was_disqualified?: boolean | null
  /** The run ids behind `was_disqualified`. Empty with a null flag = un-examined. */
  disqualified_in_runs?: string[] | null
  /**
   * The freshest ligand-starting-point lookup any run made for this target, from
   * infra/sql/020_ligand_starts.sql. Unlike everything above it this counts NO runs
   * — it is one query against the PDB and one against a compound catalog — so it
   * carries none of the corroboration caveat and none of the corroboration meaning.
   *
   * `latest_ligand_starts` is the projection of a `Target.ligand_starts`, and the
   * same read-all-three rule applies: `[]` with `'no_ligand'` is a measurement,
   * `null` with `'catalog_unreachable'` is not, and absent keys mean the migration
   * has not run. `ligand_starts_checked_at` is when — a catalog changes, so the
   * column is a snapshot and a stale one is still a finding.
   */
  latest_ligand_starts?: LigandStart[] | null
  ligand_start_count?: number | null
  ligand_start_source?: string | null
  ligand_starts_checked_at?: string | null
  /**
   * A human took this row off the board, from infra/sql/027_board_retire.sql.
   *
   * THREE-STATE for the same reason as `was_disqualified`: `null` means the row
   * predates the column and nobody has considered it, `false` means someone
   * considered it and kept it, `true` means it is retired. The default board
   * response never carries a `true` — retired rows are filtered server-side — so
   * these fields are here for the entry detail and for a client that asked for them
   * back.
   *
   * Retirement is a judgement about a *run* ("this was a harness test"), not about
   * the target, which is why it is not the `archive` tier: a scored rejection and a
   * discarded test must stay distinguishable. `retire_justification` is the only
   * record of which one this was, so render it wherever `retired` is shown.
   */
  retired?: boolean | null
  retire_justification?: string | null
  retired_at?: string | null
  retired_by?: string | null
  updated_at: string | null
}

export interface BoardPage {
  total: number
  limit: number
  offset: number
  sort: BoardSortColumn
  direction: SortDirection
  min_runs: number
  /** The tier floor in force, or `null` for the whole board. */
  tier: BoardTierFloor | null
  entries: BoardEntry[]
  /** Sent by the API with every response, deliberately. Render it. */
  runs_identified_note: string
  /**
   * The champions caveat, on the BOARD response and not only on the champions
   * routes — the rows carry `champions_rounds` and `champion_rank`, so the caveat
   * has to travel with the number a reader actually sees. Absent from a runtime
   * that predates the column.
   */
  champions_corroboration_note?: string
}

/**
 * One run's observation of a board target. Not a `Target`: the store selects
 * these joined to `leaderboard.runs` for `thread_id`/`prompt` and does not
 * project `hypothesis_id`, so reusing `Target` here would promise fields the
 * response does not carry.
 */
export interface BoardObservation {
  /**
   * `leaderboard.targets.id` — the per-run row this observation came from.
   *
   * The board's report affordance needs it: a report is scoped to
   * `(run_id, target_id)` and a board entry is a consolidation with neither, so
   * the newest observation is what names the run to assess. The id is a bigserial,
   * so it is unique across runs and report rows from different runs can be merged
   * into one map by target id without collision.
   */
  target_id: number
  run_id: string
  target_key: string
  protein: string
  accession: string | null
  pathway: string | null
  moa: string | null
  rationale: string | null
  elo: number | null
  btl_score: number | string | null
  tier: string | null
  rank: number | null
  evidence: Evidence[] | null
  created_at: string | null
  thread_id: string | null
  prompt: string | null
}

/** GET /leaderboard/board/{target_key} — the entry plus every run's own row. */
export interface BoardEntryDetail {
  entry: BoardEntry
  observations: BoardObservation[]
}

/** Must stay in sync with store.SORTABLE_BOARD_COLUMNS; 400 on anything else. */
export const BOARD_SORT_COLUMNS = [
  'runs_identified',
  'best_elo',
  'latest_elo',
  'best_btl',
  // The measured pair. Sortable server-side since infra/sql/012 and left out of
  // this list until the Score column had sub-labels to hang them on; ordering by
  // either orders by the score alone, never by score AND coverage, which is why the
  // cell keeps printing the pair.
  'latest_score',
  'best_score',
  'protein',
  'best_tier',
  'latest_tier',
  'pathway',
  'first_seen_at',
  'last_seen_at',
  // "Which targets have survived a cross-run tournament" is the question the
  // champions mode exists to answer, and an unsortable column is one a reader has
  // to page through the whole board to use. Sorting on it orders targets by
  // agreement among runs of ONE harness, which is why the caveat travels with the
  // response rather than being stated by the column header alone.
  'champions_rounds',
  'champion_elo',
  // The rows this fills are the ones no run scored — scattered through a board of
  // hundreds, and unfindable without a sort. Ordering by it orders targets by a
  // figure that may have been reconstructed by a model rather than measured, so
  // `champion_score_source` has to be visible in the row, not just in the tooltip.
  'champion_score',
  // "Which targets can we test with something we already buy" is a question a
  // chemist asks of the whole board, and paging through it to find the rows with a
  // hit is what an unsortable column costs. Sorting is over a measurement, not a
  // score — the order says which targets have orderable analogs, never which are
  // better targets.
  'ligand_start_count',
] as const

export type BoardSortColumn = (typeof BOARD_SORT_COLUMNS)[number]

/**
 * The tier floors the board may be filtered to, strongest first.
 *
 * Must stay in sync with store.TIER_ORDER — the API 400s on anything else, so a
 * drift here is a visible error rather than a silently empty board. Only the
 * current vocabulary is offered: `context/target-criteria` §7 renamed A/B/C/D to
 * these words, and the legacy spellings still on the board are matched
 * server-side by `store.TIER_ALIASES` rather than being offered as their own
 * options — a reader choosing "conditional" means the band, not the spelling.
 *
 * The values are a floor and not an exact match, which is why the control reads
 * "Tier ≥" like "Runs ≥" beside it: the bands are ordered, and "conditional or
 * stronger" is the question a board of hundreds is actually read with.
 */
export const BOARD_TIER_FLOORS = [
  'immediate',
  'conditional',
  'exploratory',
  'archive',
] as const

export type BoardTierFloor = (typeof BOARD_TIER_FLOORS)[number]
